import { useEffect, useState } from 'react';
import { MessageSquareText } from 'lucide-react';
import { Badge, Button, toast } from '@ludolio/shared-ui';
import { useLabsReview } from './context';
import { downloadTextFile, formatDate } from './format';
import { MetaItem } from './ProjectRow';
import type { LabManagedQuiz, LabManagedQuizResponses, LabQuizQuestionKind } from './types';

const QUIZ_STATUS: Record<LabManagedQuiz['status'], { label: string; variant: 'success' | 'secondary' | 'outline' }> = {
  open: { label: 'Open', variant: 'success' },
  draft: { label: 'Draft', variant: 'secondary' },
  closed: { label: 'Closed', variant: 'outline' },
};

const QUESTION_KINDS: Record<LabQuizQuestionKind, string> = {
  short_text: 'Written answer',
  single_choice: 'One choice',
  multi_choice: 'Several choices',
};

interface ProjectQuizzesProps {
  quizzes: LabManagedQuiz[] | null;
  error: string;
  canReadResponses: boolean;
}

export function ProjectQuizzes({ quizzes, error, canReadResponses }: ProjectQuizzesProps) {
  const [responsesFor, setResponsesFor] = useState<string | null>(null);

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">Quizzes</h3>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!quizzes && !error && <p className="text-sm text-muted-foreground">Loading…</p>}
      {quizzes?.length === 0 && <p className="text-sm text-muted-foreground">None</p>}
      {quizzes && quizzes.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {quizzes.map((quiz) => {
            const showingResponses = responsesFor === quiz.id;
            return (
              <li key={quiz.id} className="space-y-3 p-3">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-sm font-medium">{quiz.title}</span>
                  <Badge variant={QUIZ_STATUS[quiz.status].variant} className="px-2 py-0.5">{QUIZ_STATUS[quiz.status].label}</Badge>
                  {quiz.currentVersion && (
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-4 text-muted-foreground">
                      v{quiz.currentVersion.version}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    <MetaItem icon={MessageSquareText} label="Responses">
                      {quiz.totalResponses === 1 ? '1 response' : `${quiz.totalResponses} responses`}
                    </MetaItem>
                  </span>
                  {canReadResponses && quiz.totalResponses > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="ml-auto"
                      aria-expanded={showingResponses}
                      onClick={() => setResponsesFor(showingResponses ? null : quiz.id)}
                    >
                      {showingResponses ? 'Hide responses' : 'Responses'}
                    </Button>
                  )}
                </div>
                {quiz.draft.description && <p className="whitespace-pre-line text-sm text-muted-foreground">{quiz.draft.description}</p>}
                <ol className="space-y-3">
                  {quiz.draft.questions.map((question, index) => (
                    <li key={question.id} className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-1 text-sm">
                      <span className="text-muted-foreground">{index + 1}.</span>
                      <div className="min-w-0 space-y-1.5">
                        <p className="whitespace-pre-line">{question.prompt}</p>
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                          <span>{QUESTION_KINDS[question.kind]}</span>
                          {question.required && <span className="rounded bg-muted px-1.5 leading-5">Required</span>}
                        </div>
                        {question.options && question.options.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {question.options.map((option) => (
                              <Badge key={option.id} variant="outline" className="rounded px-1.5 py-0 text-[11px] font-normal leading-4 tracking-normal">
                                {option.label}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
                {showingResponses && <QuizResponses quiz={quiz} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function QuizResponses({ quiz }: { quiz: LabManagedQuiz }) {
  const { api, getErrorMessage } = useLabsReview();
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<LabManagedQuizResponses | null>(null);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState<'csv' | 'json' | null>(null);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError('');
    api.quizResponses(quiz.id, page)
      .then((loaded) => active && setResult(loaded))
      .catch((err) => active && setError(getErrorMessage(err)));
    return () => {
      active = false;
    };
  }, [api, getErrorMessage, page, quiz.id]);

  const download = async (format: 'csv' | 'json') => {
    setExporting(format);
    try {
      const file = await api.exportQuiz(quiz.id, format);
      downloadTextFile(file.filename, file.contentType, file.body);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setExporting(null);
    }
  };

  const responses = result?.responses;
  const pageCount = responses ? Math.max(1, Math.ceil(responses.total / responses.pageSize)) : 1;

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium">Responses{quiz.currentVersion ? ` to v${quiz.currentVersion.version}` : ''}</h4>
        {result?.canExport && (
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" disabled={exporting !== null} onClick={() => void download('csv')}>Download CSV</Button>
            <Button type="button" size="sm" variant="outline" disabled={exporting !== null} onClick={() => void download('json')}>Download JSON</Button>
          </div>
        )}
      </div>
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
      {!result && !error && <p className="text-sm text-muted-foreground">Loading responses…</p>}
      {responses?.items.length === 0 && <p className="text-sm text-muted-foreground">No responses yet</p>}
      {responses && responses.items.length > 0 && (
        <ul className="space-y-2">
          {responses.items.map((response) => (
            <li key={response.id} className="space-y-2 rounded-md bg-muted/40 p-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium">{response.respondent.name}</span>
                <span className="text-xs text-muted-foreground">{formatDate(response.submittedAt)}</span>
              </div>
              <dl className="space-y-1">
                {response.answers.map((answer) => (
                  <div key={answer.questionId} className="grid gap-1 sm:grid-cols-2">
                    <dt className="text-muted-foreground">{answer.prompt}</dt>
                    <dd className="whitespace-pre-line">{answer.answer || '—'}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      )}
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
          <Button type="button" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</Button>
          <span>{page} / {pageCount}</span>
          <Button type="button" size="sm" variant="outline" disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
